import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';
import type {
  ClusterId,
  ClustersResponse,
  Connection,
  RdsUser,
  TempUser,
  CreatedTempUser,
  CreateUserRequest,
  UpdateUserRequest,
  CreateTempUserRequest,
} from '@internal/backstage-plugin-rds-admin-common';

export interface RdsAdminApi {
  getClusters(): Promise<ClustersResponse>;
  getConnections(cluster: ClusterId): Promise<Connection[]>;
  getUsers(cluster: ClusterId): Promise<RdsUser[]>;
  createUser(cluster: ClusterId, req: CreateUserRequest): Promise<void>;
  updateUser(cluster: ClusterId, username: string, req: UpdateUserRequest): Promise<void>;
  deleteUser(cluster: ClusterId, username: string): Promise<void>;
  getTempUsers(cluster: ClusterId): Promise<TempUser[]>;
  createTempUser(cluster: ClusterId, req: CreateTempUserRequest): Promise<CreatedTempUser>;
  revokeTempUser(cluster: ClusterId, id: string): Promise<void>;
}

export const rdsAdminApiRef = createApiRef<RdsAdminApi>({
  id: 'plugin.rds-admin.service',
});

export class RdsAdminApiClient implements RdsAdminApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  private async baseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('rds-admin');
  }

  private async fetch<T>(path: string, init?: RequestInit): Promise<T> {
    const base = await this.baseUrl();
    const resp = await this.fetchApi.fetch(`${base}${path}`, init);
    if (!resp.ok) {
      const text = await resp.text().catch(() => resp.statusText);
      throw new Error(`RDS Admin API error ${resp.status}: ${text}`);
    }
    return resp.json();
  }

  async getClusters(): Promise<ClustersResponse> {
    return this.fetch<ClustersResponse>('/clusters');
  }

  async getConnections(cluster: ClusterId): Promise<Connection[]> {
    const data = await this.fetch<{ connections: Connection[] }>(
      `/clusters/${cluster}/connections`,
    );
    return data.connections;
  }

  async getUsers(cluster: ClusterId): Promise<RdsUser[]> {
    const data = await this.fetch<{ users: RdsUser[] }>(`/clusters/${cluster}/users`);
    return data.users;
  }

  async createUser(cluster: ClusterId, req: CreateUserRequest): Promise<void> {
    await this.fetch(`/clusters/${cluster}/users`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(req),
    });
  }

  async updateUser(
    cluster: ClusterId,
    username: string,
    req: UpdateUserRequest,
  ): Promise<void> {
    await this.fetch(`/clusters/${cluster}/users/${encodeURIComponent(username)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(req),
    });
  }

  async deleteUser(cluster: ClusterId, username: string): Promise<void> {
    await this.fetch(`/clusters/${cluster}/users/${encodeURIComponent(username)}`, {
      method: 'DELETE',
    });
  }

  async getTempUsers(cluster: ClusterId): Promise<TempUser[]> {
    const data = await this.fetch<{ tempUsers: TempUser[] }>(
      `/clusters/${cluster}/temp-users`,
    );
    return data.tempUsers;
  }

  async createTempUser(
    cluster: ClusterId,
    req: CreateTempUserRequest,
  ): Promise<CreatedTempUser> {
    const data = await this.fetch<{ tempUser: CreatedTempUser }>(
      `/clusters/${cluster}/temp-users`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(req),
      },
    );
    return data.tempUser;
  }

  async revokeTempUser(cluster: ClusterId, id: string): Promise<void> {
    await this.fetch(`/clusters/${cluster}/temp-users/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }
}
